// store
import * as DebtsStore from "../../../../redux/middleware/CashFlow-Debts.Middleware";
import * as ProtocolStore from "../../../../redux/middleware/MedicalProtocolMiddleware";
import * as PersonStore from "../../../../redux/middleware/Persons.Middleware";

// models
import { AppState } from "../../../../redux/reducers";
import { Debt } from "../../../../models/Entities/CashFlow/Debt/Debt";
import { Person } from "../../../../models/Entities/Person/Person";
import { Protocol } from "../../../../models/Entities/Protocols/Protocol";
import { ErrorType } from "../../../../models/HttpError";

import * as jwt_decode from "jwt-decode";

import { GetPersonByIdentifier } from "../Debt.Form/Debt.Form.Functions";

//#region Props

interface IProps {
    DebtId: number | undefined;
    isOpen: boolean;
    onClose: () => void;
}

interface ConnectedProps {
    CurrentDebt: Debt | undefined;
    CurrentProtocol: Protocol | undefined;
    Debtor: Person | undefined;
    Creditor: Person | undefined;
    OwnerId: string;

    isLoading: boolean;

    isCheckingDebtStatus: boolean;
    checkedDebtStatusId: number | undefined;
    successCheckingDebtStatus: boolean;
    debtStatusResult: boolean | undefined;

    error: ErrorType | undefined;
}

interface DispatchProps {
    GetDebtById: typeof DebtsStore.actionCreators.GetDebtById;
    CheckDebtStatus: typeof DebtsStore.actionCreators.CheckDebtStatus;
    GetAllPersons: typeof PersonStore.actionCreators.GetAllPersons;
    GetAllMedicalProtocols: typeof ProtocolStore.actionCreators.GetAllMedicalProtocols;
}

export type Props = IProps & ConnectedProps & DispatchProps;

//#endregion

export const mapStateToProps = (state: AppState, ownProps: IProps): ConnectedProps => {
    const debtorList: Debt[] = state?.CashFlowDebts?.DebtsAsDebtor ?? [];
    const creditorList: Debt[] = state?.CashFlowDebts?.DebtsAsCreditor ?? [];

    const currentDebt: Debt | undefined = ownProps.DebtId
        ? debtorList.find((d) => d.id === ownProps.DebtId) ??
          creditorList.find((d) => d.id === ownProps.DebtId)
        : undefined;

    const decoded = (state?.oidc?.user?.access_token
        ? (jwt_decode.jwtDecode(state.oidc.user.access_token) as { tenantId?: string })
        : undefined);
    const currentOwner: string = decoded?.tenantId ?? "";

    const persons: Person[] = state.Persons?.list ?? [];

    const debtor = currentDebt
        ? GetPersonByIdentifier(persons, currentOwner, currentDebt.debtor)
        : undefined;

    const creditor = currentDebt
        ? GetPersonByIdentifier(persons, currentOwner, currentDebt.creditor)
        : undefined;

    const currentProtocol: Protocol | undefined = currentDebt?.protocolId
        ? state.MedicalProtocols?.list?.find((p: Protocol) => p.id === currentDebt.protocolId)
        : undefined;

    return {
        CurrentDebt: currentDebt,
        CurrentProtocol: currentProtocol,
        Debtor: debtor,
        Creditor: creditor,
        OwnerId: currentOwner,

        isLoading: state.CashFlowDebts?.isLoadingOne ?? false,

        isCheckingDebtStatus: state.CashFlowDebts?.isCheckingDebtStatus ?? false,
        checkedDebtStatusId: state.CashFlowDebts?.checkedDebtStatusId,
        successCheckingDebtStatus: state.CashFlowDebts?.successCheckingDebtStatus ?? false,
        debtStatusResult: state.CashFlowDebts?.debtStatusResult,

        error: state.CashFlowDebts?.error,
    };
};

export const mapDispatchToProps = {
    GetDebtById: DebtsStore.actionCreators.GetDebtById,
    CheckDebtStatus: DebtsStore.actionCreators.CheckDebtStatus,
    GetAllPersons: PersonStore.actionCreators.GetAllPersons,
    GetAllMedicalProtocols: ProtocolStore.actionCreators.GetAllMedicalProtocols,
};



// helpers
export const formatMoney = (
    amount: number | undefined | null,
    currency: string | undefined,
    fallbackCurrency: string = "ARS"
): string => {
    const value = amount ?? 0;
    const code = currency || fallbackCurrency;
    try {
        return new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: code,
            minimumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${code} ${value.toFixed(2)}`;
    }
};
